import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Who is signed into the back office.
 *
 * The token is kept in `sessionStorage` rather than `localStorage`: it is short
 * lived anyway, and this way closing the tab ends the session instead of leaving
 * a usable credential behind on a shared machine.
 */
const STORAGE_KEY = 'goaround.admin.session';

export interface AdminSession {
  accessToken: string;
  username: string;
  role: 'ADMIN' | 'SUPER_ADMIN';
}

interface AuthState {
  session: AdminSession | null;
}

function restore(): AdminSession | null {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as AdminSession) : null;
  } catch {
    // A corrupted entry should sign the user out, not break the app.
    return null;
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState: { session: restore() } as AuthState,
  reducers: {
    signedIn(state, action: PayloadAction<AdminSession>) {
      state.session = action.payload;
      sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(action.payload));
    },
    signedOut(state) {
      state.session = null;
      sessionStorage?.removeItem(STORAGE_KEY);
    },
  },
  selectors: {
    selectSession: (state: AuthState) => state.session,
    selectIsSignedIn: (state: AuthState) => state.session !== null,
    selectCanManageAdministrators: (state: AuthState) =>
      state.session?.role === 'SUPER_ADMIN',
    /**
     * Whether the signed-in user may open the Settings tab at all.
     *
     * Unlike {@link selectCanManageAdministrators} (still SUPER_ADMIN-only, and
     * still what gates the Users/Theme/About Us tabs), an ordinary ADMIN is
     * allowed into Settings too — just restricted once there to the
     * Contacts/Social Profiles/Branding sections the backend lets them reach.
     */
    selectCanAccessSettings: (state: AuthState) =>
      state.session?.role === 'ADMIN' || state.session?.role === 'SUPER_ADMIN',
  },
});

export const { signedIn, signedOut } = authSlice.actions;
export const {
  selectSession,
  selectIsSignedIn,
  selectCanManageAdministrators,
  selectCanAccessSettings,
} = authSlice.selectors;

/** The token, for the API layer to attach. */
export const selectAccessToken = (state: { auth: AuthState }) =>
  state.auth.session?.accessToken;

export default authSlice;
